import { useEffect } from 'react'
import ROUTES from '../consts/paths'
import { preparedRoutes } from '../hooks/useAllowedPath'
import { getLoading, getPagePermissionError, getPagePermissions } from '../store/slices/roleSlice/selectors'
import { useAppDispatch } from '../store'
import { getUserPagePermissionsApi } from '../store/slices/roleSlice/api'
import useAsyncEffect from 'use-async-effect'
import { CURRENT_ROLE_ID_KEY } from '../consts/roles'
import { notification } from '../components/Notifications'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthenticatedContext } from '../hooks/AuthenticatedContext'
import { useUserProfile } from '../hooks/useUserProfile'
import { getPriorityBaseRoute } from '../helpers/navigationHelpers'

export const useAppRoutesPermissionsAndRoles = (): { shouldShowLoading: boolean, isOnPublicRoute: boolean } => {
  const { isAuthed, isLoading } = useAuthenticatedContext()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const pagePermissionError = getPagePermissionError()
  const pagePermissions = getPagePermissions()
  const permissionLoading = getLoading()

  const isOnPublicRoute = (
    location.pathname === ROUTES.LOGIN ||
    location.pathname === ROUTES.EDLINK_LOGIN ||
    location.pathname === ROUTES.LOGOUT ||
    location.pathname === ROUTES.SURVEY_COMPLETED_PUBLIC ||
    location.pathname.startsWith(ROUTES.PUBLIC_SURVEY_PAGE)
  )

  const { data: userProfile, isLoading: isUserProfileLoading, isPending: isUserProfilePending, isError: isErrorInUserProfileApi, error: errorsOnUserProfile, refetch } = useUserProfile()
  const userRoles = userProfile?.roles

  useEffect(() => {
    if (isAuthed && !isLoading && !isOnPublicRoute) {
      void refetch()
    }
  }, [isAuthed, isLoading, isOnPublicRoute])

  useEffect(() => {
    if (!isLoading && !isAuthed && !isOnPublicRoute) {
      navigate(ROUTES.LOGOUT)
    }
  }, [isLoading, isAuthed, isOnPublicRoute])

  useEffect(() => {
    if (isErrorInUserProfileApi) {
      notification('Failed to load user. Please log in again.', { variant: 'error' })
      navigate(ROUTES.LOGOUT)
    }
  }, [isErrorInUserProfileApi])

  useEffect(() => {
    const isUserAuthedButPermissionsFailed = isAuthed && pagePermissionError && userRoles && userRoles.length > 0
    if (isUserAuthedButPermissionsFailed) {
      localStorage.setItem('redirectLoginUrl', location.pathname)
      navigate(location.state?.fromLogin ? ROUTES.PERMISSION_DENIED : ROUTES.LOGOUT)
    }
  }, [isAuthed, userRoles, pagePermissionError])

  const shouldFetchPermissions = (): boolean => {
    // Only fetch if user is authed, has roles, and profile is not pending
    if (!isAuthed || !userRoles || userRoles.length === 0 || isUserProfilePending) return false

    // Only fetch if permissions are not loaded, no error, and tenantKey exists
    const userDoesNotHavePermissions = !pagePermissions && !pagePermissionError && !!localStorage.getItem('tenantKey')
    if (!userDoesNotHavePermissions) return false

    // Only fetch if user has a role header or roles
    const hasRoleIdHeader = !!localStorage.getItem(CURRENT_ROLE_ID_KEY)
    const userHaveRoles = hasRoleIdHeader || (Array.isArray(userRoles) && userRoles.length > 0)
    if (!userHaveRoles || isUserProfileLoading || isUserProfilePending) return false

    return true
  }

  useAsyncEffect(() => {
    if (shouldFetchPermissions() && !isOnPublicRoute) {
      void dispatch(getUserPagePermissionsApi({}))
    }
  }, [isAuthed, userRoles, isUserProfileLoading, isUserProfilePending, pagePermissions, pagePermissionError])

  useAsyncEffect(async () => {
    if (isAuthed && !isOnPublicRoute) {
      const path = location.state?.from?.pathname ?? location.pathname + location.search
      const validPaths = pagePermissions?.flatMap(page => preparedRoutes[page]).filter(Boolean)
      const uniqueValidPaths = [...new Set(validPaths)]

      const handleNavigation = (): void => {
        if (path && path === ROUTES.NOT_FOUND) {
          return
        }

        // Only deny when permissions finished loading and are truly empty.
        if (!permissionLoading && Array.isArray(pagePermissions) && pagePermissions.length === 0) {
          navigate(ROUTES.PERMISSION_DENIED)
          return
        }

        const validRoute = uniqueValidPaths?.find(route => path.startsWith(route)) && path
        const currentRoleId = localStorage.getItem(CURRENT_ROLE_ID_KEY)
        const currentRole = userProfile?.roles?.find(role => role.roleId === currentRoleId)
        const priorityBaseRoute = getPriorityBaseRoute(currentRole?.roleName, uniqueValidPaths)

        if (validRoute) {
          return
        }

        if (priorityBaseRoute) {
          navigate(priorityBaseRoute, { replace: true })
          return
        }

        if (!permissionLoading && pagePermissions && pagePermissions.length > 0) {
          navigate(ROUTES.NOT_FOUND, { replace: true })
        }
      }

      handleNavigation()
    }
  }, [isAuthed, pagePermissions, location.pathname, userRoles])

  const shouldShowLoading = (permissionLoading || isUserProfileLoading)

  return { shouldShowLoading, isOnPublicRoute }
}
